import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { MatSnackBar } from '@angular/material/snack-bar';
import { BehaviorSubject, filter, lastValueFrom, take, timeout } from 'rxjs';
import { Router } from '@angular/router';
import { MatDialog, MatDialogRef } from '@angular/material/dialog';
import { ReAuthComponent } from './re-auth/re-auth.component';
import { Preferences } from '@capacitor/preferences';
import { BaseHttpService } from './base-capacitor-http';
import { LoginComponent } from './login/login.component';
import { MemberLoginComponent } from './member-login/member-login.component';
import { Capacitor } from '@capacitor/core';
import { SecureStoragePlugin } from 'capacitor-secure-storage-plugin';
import { environment } from '../../environments/environment';

export class UpdateResponse {
  message: string;
  result: boolean;
}

export class EncryptedData {
  payload: string;
  iv: string;
}

export class UserInfo {
  uid: string;
  username: string;
  maskedEmail: string;
  role: string;
  emailVerified: boolean;
  isDisabled: boolean;
}

export class JwtPayload {
  sub: string;
  anonymous: boolean;
  type: string;
  role: string;
  exp: number;
  ait: number;
  disabled: boolean;
  id: number;
  verified: boolean;
  instance: number;
  sub_status: string;
  sub_exp: number;
}

export class AuthResponse {
  uid: string;
  authenticated: boolean;
  guest?: boolean;
  member: boolean;
  worker: boolean;
  chatId: string;
  verified?: boolean;
  disabled?: boolean;
  username: string;
  token: string;
  type: string;
  expires: Date;
  error?: string;
  message?: string;
  instance: number;
  subStatus: string;
  subExpires: number;
  role: string;
}

export class RegisterCredentials {

  username: string;
  password: string;
  confirmPassword: string;
  email: string;
  role: string;
}

export class LoginCredential {
  email: string;
  password: string;
}


@Injectable({
  providedIn: 'root'
})
export class AuthService extends BaseHttpService {

  private baseURL: string;
  private urlEndPoint: string;


  private authState = new BehaviorSubject<AuthResponse | null>(null);

  constructor(client: HttpClient, snackBar: MatSnackBar, private dialog: MatDialog, private router: Router) {
    super(client, snackBar);
    this.baseURL = environment.server.mainURL;
  }


  async openLoginDialog() {
    const dialogRef: MatDialogRef<LoginComponent, AuthResponse> =
      this.dialog.open(LoginComponent, {
        width: '700px',
        height: '520px',
        maxWidth: '100vw',
      });
    return lastValueFrom(dialogRef.afterClosed());
  }

  async openMemberLogin() {
    const dialogRef: MatDialogRef<MemberLoginComponent, AuthResponse> =
      this.dialog.open(MemberLoginComponent, {
        width: '700px',
        height: '520px',
        maxHeight: '100vw'
      });
    return lastValueFrom(dialogRef.afterClosed());
  }

  async openReauthDialog() {
    const dialogRef: MatDialogRef<ReAuthComponent, AuthResponse> =
      this.dialog.open(ReAuthComponent, {
        width: '450px',
        height: '500px',
        maxWidth: '100vw',
      });

    return lastValueFrom(dialogRef.afterClosed());
  }

  async getUserRole() {
    const response = await this.getAuthResponse();
    return response.role;
  }

  async getActiveInstance() {
    const response = await this.getAuthResponse();
    
    return response.instance;
  }

  updateAuthState(value: AuthResponse) {
    this.authState.next(value);
  }
  async getAuthResponse(){
   
    return lastValueFrom(this.authState.pipe(
      filter(response => response !== null),
      take(1),
      timeout(5000)
    ));
  }


  getAuthState() {
    return this.authState.asObservable();
  }

  
  async getUserData() {
    this.urlEndPoint = this.baseURL + 'users/info';
    const encryptedData: EncryptedData = await this.get<EncryptedData>(this.urlEndPoint);
    return this.decryptResponse<UserInfo>(encryptedData.payload, this.KEY, encryptedData.iv);
  }
    

  async checkAccountStatus(): Promise<AuthResponse> {
    this.urlEndPoint = this.baseURL + 'commons/users/account-status';
    return this.get<AuthResponse>(this.urlEndPoint);
  }

  async updateUsername(username: string): Promise<UpdateResponse> {
    this.urlEndPoint = this.baseURL + 'users/username-update';
    let body = {
      username: username
    }
    return this.post<UpdateResponse>(this.urlEndPoint, body);
  }

  async updatePassword(password: string, newPassword: string) {
    this.urlEndPoint = this.baseURL + 'users/update-password';
    let body = {
      password: password,
      newPassword: newPassword
    }
    return this.post<boolean>(this.urlEndPoint, body);
  }

  async accountDeletionRequest() {
    this.urlEndPoint = this.baseURL + 'users/deletion-request';
    let body = {
      url: environment.server.domain
    };

    return this.post<UpdateResponse>(this.urlEndPoint, body);
  }

  async getUserHashedEmail() {
    this.urlEndPoint = this.baseURL + 'users/masked-email';
    return this.get<{ email: string }>(this.urlEndPoint);
  }

  /**
   * Open auth endpoint
   * 
   * 
   */



  async register(credential: RegisterCredentials): Promise<AuthResponse> {
    this.urlEndPoint = this.baseURL + 'commons/users/register';

    let body = {
      payload: this.encrypt(credential, this.KEY),
    }

    return this.post<AuthResponse>(this.urlEndPoint, body);
  }

  async login(credential: LoginCredential) {
    this.urlEndPoint = this.baseURL + 'commons/users/login';

    let body = {
      payload: this.encrypt<LoginCredential>(credential, this.KEY),
    }

    return this.post<AuthResponse>(this.urlEndPoint, body);
  }

  /**
   * sends email to user with user name
   * @param email 
   * @returns 
   */
  async forgotUserName(email: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/forgot-username';
    let bod = {
      email: email
    }
    let body = {
      paload: this.encrypt(bod, this.KEY)
    }
    return this.post(this.urlEndPoint, body);
  }

  async verifyEmail(token: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/verify-email';
    const params = {
      token: token
    }
    return this.get<AuthResponse>(this.urlEndPoint, params);
  }

  async confirmAccountDeletion(token: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/confirm-deletion';
    let body = {
      url: environment.server.domain,
      token: token
    }

    return this.post<boolean>(this.urlEndPoint, body);
  }

  async passwordReset(password: string, confirmPassword: string, authToken: AuthResponse) {
    this.urlEndPoint = this.baseURL + 'commons/users/password-reset';
    let bod = {
      password: password,
      confirmPassword: confirmPassword,
      url: environment.server.domain
    }

    let body = {
      payload: this.encrypt(bod, this.KEY)
    }

    let params = {
      token: authToken.token
    }

    return this.post<boolean>(this.urlEndPoint, body, params);
  }

  async passwordResetRequest(email: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/password-reset-request';
    let bod = {
      email: email,
      url: environment.server.domain
    };

    let body = {
      payload: this.encrypt(bod, this.KEY)
    }


    return this.post<boolean>(this.urlEndPoint, body);
  }

  async emailUpdateRequest(currentEmail: string, newEmail: string, confirmEmail: string, password: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/email-update-request';
    let bod = {
      currentEmail: currentEmail,
      newEmail: newEmail,
      confirmEmail: confirmEmail,
      password: password,
      url: environment.server.domain
    };

    let body = {
      payload: this.encrypt(bod, this.KEY)
    };

    return this.post<boolean>(this.urlEndPoint, body);
  }


  async emailUpdate(token: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/update-email';
    let body = {
      url: environment.server.domain
    };

    return this.post<boolean>(this.urlEndPoint, body, { token: token });
  }

  async revertEmail(token: string) {
    this.urlEndPoint = this.baseURL + 'commons/users/revert-email';
    let body = {
      url: environment.server.domain
    };
    return this.post<boolean>(this.urlEndPoint, body, { token: token });
  }

  async validateToken(token: string): Promise<AuthResponse> {
    this.urlEndPoint = this.baseURL + 'commons/users/token-verify';
    let body = {
      token: token
    };
    return this.post<AuthResponse>(this.urlEndPoint, body);
  }

  async emailVerificationRequest() {
    this.urlEndPoint = this.baseURL + 'users/email-verification-request';
    let body = {
      url: environment.server.domain
    };

    return this.post(this.urlEndPoint, body);
  }


  /** 
  * @param credential 
  * @returns 
  */

  async reAuthenticate(credential: LoginCredential) {
    this.urlEndPoint = this.baseURL + 're-authenticate';

    let body = {
      payload: this.encrypt<LoginCredential>(credential, this.KEY)
    }

    return this.post<AuthResponse>(this.urlEndPoint, body);
  }


  async logout() {
    this.urlEndPoint = this.baseURL + 'commons/users/logout';
    const response = await this.delete<AuthResponse>(this.urlEndPoint);
    this.authState.next(response);
    this.router.navigate(['/']);
  }


  async guestLogin() {

    this.urlEndPoint = this.baseURL + 'commons/users/guest';
    return this.post<AuthResponse>(this.urlEndPoint, {});
  }

  async memberLogin(credential: LoginCredential) {
    this.urlEndPoint = this.baseURL + 'commons/members/login';
    let body = {
      payload: this.encrypt<LoginCredential>(credential, this.KEY)
    }
    return this.post<AuthResponse>(this.urlEndPoint, body);
  }

  async reAuthUser() {
    let response = await this.openReauthDialog();
    if (response) {
      this.storeAuthToken(response);
      return response;
    }
    return null;
  }

  async storeVerificationToken(token: AuthResponse): Promise<AuthResponse> {
    if (Capacitor.isNativePlatform()) {
      const json = JSON.stringify(token);
      try {
        await SecureStoragePlugin.set({ key: this.VERIFICATION, value: json });
        return token;
      } catch (err) {
        return null;
      }

    }
    const tokenResponse: string = this.encryptPayLoad<AuthResponse>(token, this.KEY);
    await Preferences.set({ key: this.VERIFICATION, value: tokenResponse });
    return token;
  }

  /**
   * Retrieves verification token used in email links from session storage
   * @returns 
   */

  async getVerificationToken(): Promise<AuthResponse> {

    if (Capacitor.isNativePlatform()) {
      try {

        const response = await SecureStoragePlugin.get({ key: this.AUTH_TOKEN });

        if (!response?.value) {
          console.log('retrieve no value');
          return null;
        }

        let parsed: unknown;
        try {
          parsed = JSON.parse(response.value);
        } catch (err) {
          console.log('Retrieve err: ', err);
          return null;
        }


        // Ensure it's actually an object (and not boolean/number/etc)
        if (typeof parsed !== 'object' || parsed === null) {
          console.log('No object');
          return null;
        }
        return parsed as AuthResponse;

      } catch (err) {
        console.log('Retireve err: ', err);
        return null;
      }
    }

    let { value } = await Preferences.get({ key: this.VERIFICATION });
    if (value) {
      let response = this.decryptPayload<AuthResponse>(value, this.KEY);

      return response;
    }
    return null;
  }

  async removeVerificationToken() {
    if (Capacitor.isNativePlatform()) {
      await SecureStoragePlugin.remove({ key: this.AUTH_TOKEN });
      return;
    }
    await Preferences.remove({ key: this.AUTH_TOKEN });
  }
}
